import { Link } from 'react-router-dom';
import { useHeldClients } from '../api/standing.ts';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { formatDate } from '../dates.ts';

/**
 * Clients held at load (102): the load could not appoint them without a person
 * reading the court order. Their own list, because a held client is waiting
 * on a person and a prospect is not; on the client list they would read as
 * Pipeline, which is how 0115 sat unnoticed for a week. Recording the
 * appointment from the order ends a hold. Row security decides who is listed.
 */
export function HeldClients() {
  const list = useHeldClients();
  const rows = list.data?.held;
  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="page-head">
          <h1 className="page-title">Held at load</h1>
          <p className="mt-0.5 max-w-[60rem] text-sm text-neutral-500">
            Clients the load could not appoint without somebody reading the court order. Each is waiting
            on a person. Record the appointment from the order and the hold ends. Clients who have died or
            been discharged are marked and listed last.
          </p>
        </header>
        <div className="page-body">
          {list.error && !rows ? (
            <LoadFailure what="The clients held at load" error={list.error}
                         onRetry={() => void list.refetch()} retrying={list.isFetching} />
          ) : !rows ? (
            <p className="px-4 py-10 text-center text-neutral-600">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="px-4 py-10 text-center text-neutral-600">None. No client is held.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="border-b border-neutral-200 text-xs font-bold tracking-[0.1em] text-neutral-500 uppercase">
                  <tr>
                    <th className="px-4 py-2 lg:px-5">Client</th>
                    <th className="px-3 py-2">Why it is held</th>
                    <th className="px-3 py-2">Since</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.client_id} className="border-b border-neutral-100 align-top" data-quick-client={r.client_id}>
                      <td className="px-4 py-2 lg:px-5">
                        <Link to={`/clients/${r.client_id}`} state={{ from: 'Held at load' }}
                              className="inline-flex min-h-[44px] items-center text-neutral-900 underline-offset-2 hover:underline">
                          {r.display_name}
                        </Link>
                        <span className="block text-sm text-neutral-500">{r.client_ref} · {r.legal_status}</span>
                        {(r.deceased || r.discharged) && (
                          <span className="mt-1 block text-sm font-semibold text-neutral-700">
                            Held at load, client {r.deceased ? 'deceased' : 'discharged'}
                            <span className="block font-normal text-neutral-500">
                              The order still wants reading, but not urgently.
                            </span>
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-neutral-800">{r.reason}</td>
                      <td className="px-3 py-2 text-neutral-600">{formatDate(r.held_on)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
